import React, { FC, useState } from "react"
import { Dialog, DialogContent, DialogActions, DialogHeader, Icon, Button, useIntl } from "@metsooutotec/modes-react-components"
import customerStore from "../store/customer-store"
import { NavigationDeleteEntityOptions } from "../../../../components/utils/modal-helper"
import { ModalTypes } from "../utils/customer-modal-helper"
import { Customer } from "../models/customer-models"
import { Site } from "features/organizations/sites/models/sites-models"
import { Asset } from "features/organizations/assets/models/asset-models"
import { AssetSection } from "features/organizations/asset-sections/models/asset-section-models"

interface DeleteEntityProps {
    isOpen: boolean | undefined
    onClose: (params: NavigationDeleteEntityOptions) => void
    entity?: Customer | Site | Asset | AssetSection
    modalType: ModalTypes
    customerId: string
    siteId?: string
    assetId?: string
}

const EntityDeleteModal: FC<DeleteEntityProps> = ({ isOpen, onClose, entity, modalType, customerId, siteId, assetId }) => {
    const { formatMessage } = useIntl()
    const [isLoading, setLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<{ isError: boolean; errorMessage: string }>({ isError: false, errorMessage: "" })
    let header, content, childObjects

    switch (modalType) {
        case ModalTypes.CustomerModal:
            header = formatMessage("customerDelete.header")
            content = formatMessage("customerDelete.content")
            childObjects = formatMessage("customerDelete.childObjects")
            break
        case ModalTypes.SiteModal:
            header = formatMessage("siteDelete.header")
            content = formatMessage("siteDelete.content")
            childObjects = formatMessage("siteDelete.childObjects")
            break
        case ModalTypes.AssetModal:
            header = formatMessage("assetDelete.header")
            content = formatMessage("assetDelete.content")
            childObjects = formatMessage("assetDelete.childObjects")
            break
        case ModalTypes.AssetSectionModal:
            header = formatMessage("assetSectionDelete.header")
            content = formatMessage("assetSectionDelete.content")
            childObjects = ""
            break
        default:
            header = ""
            content = ""
            childObjects = ""
            break
    }

    const labels = {
        header,
        content,
        childObjects,
    }

    const onConfirm = async (e: React.FormEvent): Promise<void> => {
        try {
            setLoading(true)

            let passed: boolean = false
            let message: string = ""

            const result = await customerStore.deleteEntity(entity?.id as string, modalType, customerId, siteId as string, assetId as string)
            passed = result.passed
            message = result.message

            if (!passed) {
                setLoading(false)
                setIsError({ isError: true, errorMessage: message })
                return
            }

            // Handle success
            console.log("Form submitted successfully!", message)
            setLoading(false)
            const parm: NavigationDeleteEntityOptions = {
                deleted: true,
                entityName: entity?.name,
            }
            onClose(parm)
        } catch {
        } finally {
            setLoading(false)
        }
    }
    function renderSitesAndAssetsAndAssetSections() {
        if (modalType === ModalTypes.CustomerModal) {
            const customer = entity as Customer
            return (
                <>
                    {customer?.sites && customer?.sites?.length > 0 && labels.childObjects}
                    <div style={{ marginTop: "var(--size-xl)" }}>
                        {customer?.sites?.map(site => (
                            <p key={site.id}>
                                <Icon name='icon-location-24' /> {site.name}
                            </p>
                        ))}
                        {customer?.sites
                            ?.flatMap(site => site.assets || [])
                            .map(asset => (
                                <div key={asset.id}>
                                    <p>
                                        <Icon name='icon-equipment-24' /> {asset.name}
                                    </p>
                                    {asset?.assetSections?.map(assetSection => (
                                        <p key={assetSection.id}>
                                            <Icon name='icon-module-24' /> {assetSection.name}
                                        </p>
                                    ))}
                                </div>
                            ))}
                    </div>
                </>
            )
        } else if (modalType === ModalTypes.SiteModal) {
            const site = entity as Site
            return (
                <>
                    {site?.assets && site?.assets?.length > 0 && labels.childObjects}
                    <div style={{ marginTop: "var(--size-xl)" }}>
                        {site?.assets?.map(site => (
                            <p key={site.id}>
                                <Icon name='icon-equipment-24' /> {site.name}
                            </p>
                        ))}
                        {site?.assets
                            ?.flatMap(asset => asset.assetSections || [])
                            .map(assetSection => (
                                <p key={assetSection.id}>
                                    <Icon name='icon-module-24' /> {assetSection.name}
                                </p>
                            ))}
                    </div>
                </>
            )
        } else if (modalType === ModalTypes.AssetModal) {
            const asset = entity as Asset
            return (
                <>
                    {asset?.assetSections && asset?.assetSections?.length > 0 && labels.childObjects}
                    <div style={{ marginTop: "var(--size-xl)" }}>
                        {asset?.assetSections?.map(asset => (
                            <p key={asset.id}>
                                <Icon name='icon-module-24' /> {asset.name}
                            </p>
                        ))}
                    </div>
                </>
            )
        } else {
            ;<></>
        }
    }
    return (
        <>
            <Dialog open={isOpen} dimmer size='small' style={{ maxWidth: "696px" }}>
                <DialogHeader>{labels.header}</DialogHeader>
                {isError.isError && <p style={{ color: "red" }}>{isError.errorMessage}</p>}
                <DialogContent data-cy='dialog-content'>
                    {labels.content} '{entity?.name}'. {renderSitesAndAssetsAndAssetSections()}
                </DialogContent>
                <DialogActions>
                    <Button disabled={isLoading} onClick={() => onClose({ deleted: false })} subtle={true}>
                        Cancel
                    </Button>
                    <Button disabled={isLoading} loading={isLoading} onClick={onConfirm} primary={true}>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default EntityDeleteModal
